import {
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/hooks/auth.hook';
import { useRootSelector } from '@/hooks/selector.hook';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import mockAvatar from '@/assets/images/mockAvatar.png';
import NexgenBrosLogo from '@/assets/svg/LogoLandscape.svg';
import { sideBarItem } from './constants';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '../ui/button';
import { LogOutIcon } from './icon';

export const AppSidebar = () => {
  const currentPageTitle = usePageTitle() as unknown as string | null;
  const { fetchProfile, logout } = useAuth();
  const { user, token } = useRootSelector((state) => state.auth);
  const { isMobile, setOpenMobile } = useSidebar();

  useEffect(() => {
    if (token && !user) {
      fetchProfile();
    }
  }, [token, user, fetchProfile]);

  // Function to check if menu item is active
  const isActive = (pageTitle: string | null) => {
    if (!pageTitle) return false;
    return currentPageTitle === pageTitle;
  };

  // Function to handle sidebar item click on mobile
  const handleSidebarItemClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar collapsible="icon" className="bg-box h-screen border-r border-main">
      <SidebarContent className="!gap-0 !p-0">
        <div className="flex flex-col h-full bg-box">
          {/* Logo */}
          <div className="flex-shrink-0">
            <img
              src={NexgenBrosLogo}
              alt="Nexgen Bros"
              className="pt-[2rem] lg:pt-[3.3rem] pb-[1rem] lg:pb-[1.3rem] border-b border-main px-[2.4rem]"
            />
          </div>

          {/* Menu Items - Scrollable */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-[2.4rem] pt-[1.2rem] lg:pt-[1.8rem] pb-[0.4rem]">
            <SidebarMenu className="flex flex-col gap-[0.8rem] lg:gap-[1.2rem]">
              {sideBarItem.map((item) => {
                const IconComponent = item.icon;
                const itemIsActive = isActive(item.label);

                return (
                  <SidebarMenuItem key={item.route}>
                    <SidebarMenuButton
                      asChild
                      isActive={itemIsActive}
                      className="px-[1.6rem] data-[active=true]:bg-interactive data-[active=true]:text-[var(--color-background-action-primary)] py-[0.8rem] lg:py-[1.2rem] h-[4rem] lg:h-[4.8rem] rounded-[1rem] font-normal text-[1.6rem] leading-[2.4rem] tracking-[0px] text-center"
                    >
                      <Link to={item.route} onClick={handleSidebarItemClick}>
                        <IconComponent className="!h-[2rem] !w-[2rem]" />
                        {item.label}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </div>

          {/* Profile & Logout - Always visible at bottom */}
          <div className="flex-shrink-0 border-t border-main">
            <div className="px-[1.6rem] py-[0.8rem] lg:py-[1.2rem] flex gap-[1.2rem] items-center">
              <Avatar className="w-[3rem] h-[3rem] lg:w-[3.5rem] lg:h-[3.5rem]">
                <AvatarImage src={user?.avatar ? user?.avatar : mockAvatar} alt="Bong-chan" />
                <AvatarFallback>{user?.userName}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-[0.3rem] lg:gap-[0.5rem]">
                <p className="font-medium text-[1.3rem] lg:text-[1.4rem] leading-5">
                  {user?.fullName}
                </p>
                <p className="font-roboto text-[1.2rem] leading-4 sub-text">{user?.email}</p>
              </div>
            </div>

            <div>
              <Button
                className="!bg-transparent text-normal w-full text-[1.6rem] gap-[1.2rem] !h-auto py-[1rem] lg:py-[1.4rem] px-[1.6rem] !justify-start flex items-center leading-6 text-center rounded-[1rem] hover:bg-interactive hover:text-[var(--color-background-action-primary)] transition-colors"
                onClick={() => logout()}
              >
                <LogOutIcon className="!h-[2rem] !w-[2rem]" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </SidebarContent>
    </Sidebar>
  );
};
